import Loader from '../loader.mjs';
import {
	create,
	registerCustomElement,
	isAttribute,
	animateElementHeight,
	parseAttributeValue,
	dispatchResizeDebounced,
	deepEqual,
} from '../utils.mjs';
import EVENTS from '../events.mjs';
import './picker.scss';
import { I18n } from '../helpers/i18n.mjs';
import EventManager from '../eventManager.mjs';
import { removeTitle, setTitleIfTextOverflows } from '../helpers/text-overflow-title.mjs';

export class Option extends HTMLElement {
	constructor() {
		super();

		this.onmouseenter = () => setTitleIfTextOverflows(this, 'left', 'right');
		this.onmouseleave = () => removeTitle(this);
	}

	static get observedAttributes() {
		return ['selected'];
	}

	/**
	 * Called when any of observed attributes is changed.
	 *
	 * @param {String} name Attribute name
	 * @param {*} oldValue Old value
	 * @param {*} newValue New value
	 */

	attributeChangedCallback(name, oldValue, newValue) {
		// Remove temporary value once unselected
		if (this.temporary && name === 'selected' && newValue == null) {
			this.parentNode.removeChild(this);
		}
	}

	get selected() {
		return isAttribute(this, 'selected');
	}

	set selected(value) {
		if (value) {
			this.setAttribute('selected', '');
		} else {
			this.removeAttribute('selected');
		}
	}

	get temporary() {
		return isAttribute(this, 'temporary');
	}

	set temporary(value) {
		value ? this.setAttribute('temporary', '') : this.removeAttribute('temporary');
	}

	get value() {
		const value = this.getAttribute('value');

		if (value === null) return;
		// We use JSON by default but the value can be rendered into DOM and probably it wont be in JSON
		return parseAttributeValue(value, this.getAttribute('content-type'));
	}

	set value(value) {
		if (typeof value !== 'undefined') {
			this.setAttribute('content-type', 'application/json');
			this.setAttribute('value', JSON.stringify(value));
		} else {
			this.removeAttribute('content-type');
			this.removeAttribute('value');
		}
	}
}

export class OptionGroup extends HTMLElement {
	constructor() {
		super();
	}
}

export class Picker extends HTMLElement {
	constructor() {
		super();

		this._built = false;
		this._loaded = false;
		this._options = {};
		this._actionOption = null;
		this._defaultClassList = ['form-control'];
		this._inputField = null; // Input the picker is attached to

		this.components = {}; // Custom components
		this.showSearcher = 10;
		this.showSelectAll = 3;

		// Watch for options being added to our DOM
		const observer = new MutationObserver((mutationList) => {
			const additions = new Set();
			const removals = new Set();

			for (const mutation of mutationList) {
				for (const node of mutation.removedNodes) {
					if (node.nodeName === 'IMT-OPTGROUP' || node.nodeName === 'IMT-OPTION') {
						removals.add(node);
					}
				}

				for (const node of mutation.addedNodes) {
					// Only consume imt-optgroup and imt-option nodes
					if (node.nodeName === 'IMT-OPTGROUP' || node.nodeName === 'IMT-OPTION') {
						additions.add(node);
					}
				}
			}

			// Nodes that appear in both sets were reordered, not newly added — ignore them
			for (const node of removals) {
				additions.delete(node);
			}

			// Ignore rest if there're no additions we're interested about
			if (!additions.size) return;

			// Make sure we have been built
			if (!this._built) this._build();

			const options = this.querySelector(':scope .options');
			const temporaries = this.querySelectorAll('imt-option[temporary]');

			for (const node of additions) {
				// Move node under dropdown > options
				options.appendChild(node);

				if (temporaries.length) {
					// Check for temporary items if we can remove them
					const opts = node.nodeName === 'IMT-OPTION' ? [node] : node.querySelectorAll('imt-option');

					for (const opt of opts) {
						for (const temporary of temporaries) {
							if (temporary.parentNode && temporary !== opt && temporary.value === opt.value) {
								// If temporary option was selected, select the new one instead
								opt.selected = temporary.selected;

								// Remove temporary option
								temporary.parentNode.removeChild(temporary);

								// Break out from the loop of temporary options
								break;
							}
						}
					}
				}
			}

			// Update our display
			this.update();
		});

		observer.observe(this, {
			childList: true,
		});
	}

	static get observedAttributes() {
		return ['placeholder'];
	}

	/**
	 * Normalizes and merges options.
	 * @param {object|array|string} options - new options
	 * @param {object} currentOptions - old options, provide value if you want to merge new options with the existing options
	 * @return {object} - normalized options
	 */

	static normalizeOptions(options, currentOptions = {}) {
		if (!options) return { store: [] };

		try {
			options = JSON.parse(options);
			// eslint-disable-next-line no-unused-vars
		} catch (ex) {}

		if ('object' === typeof options && !Array.isArray(options)) {
			Object.assign(currentOptions, options);
		}

		if (Array.isArray(options) || typeof options === 'string') {
			currentOptions.store = options;
		} else if (!options) {
			currentOptions.store = [];
		} else {
			currentOptions = options;
		}

		return currentOptions;
	}

	/**
	 * Called when any of observed attributes is changed.
	 *
	 * @param {String} name Attribute name
	 * @param {*} oldValue Old value
	 * @param {*} newValue New value
	 */

	attributeChangedCallback(name, oldValue, newValue) {
		this.update();
	}

	connectedCallback() {
		this._build();
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set disabled(value) {
		if (value) {
			this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}
	}

	get multiple() {
		return this.hasAttribute('multiple');
	}

	get _isMultiselectDropdown() {
		return (this.multiple || this.list) && this.dropdown;
	}

	set multiple(value) {
		if (value) {
			this.setAttribute('multiple', '');
		} else {
			this.removeAttribute('multiple');
		}
	}

	get dropdown() {
		return this.hasAttribute('dropdown');
	}

	set dropdown(value) {
		if (value) {
			this.setAttribute('dropdown', '');
		} else {
			this.removeAttribute('dropdown');
		}
	}

	get list() {
		return this.hasAttribute('list');
	}

	set list(value) {
		if (value) {
			this.setAttribute('list', '');
		} else {
			this.removeAttribute('list');
		}
	}

	get readonly() {
		return this.hasAttribute('readonly');
	}

	set readonly(value) {
		if (value) {
			this.setAttribute('readonly', '');
		} else {
			this.removeAttribute('readonly');
		}
	}

	get value() {
		if (this.multiple) {
			return Array.prototype.map.call(this.querySelectorAll('imt-option[selected]'), (node) => node.value);
		} else {
			const selected = this.querySelector('imt-option[selected]');

			if (!selected) {
				const emptyOption = Array.isArray(this.options?.store) ? this.options.store.find((x) => x.empty) : null;

				return emptyOption ? emptyOption.value : undefined;
			}
			return selected.value;
		}
	}

	set value(value) {
		const preventDefault = !EventManager.dispatch(this, EVENTS.PICKER.CHANGE, { cancelable: true });

		if (preventDefault) return;

		for (const node of this.querySelectorAll('imt-option:not(.action-option > imt-option)')) {
			if (this.multiple && Array.isArray(value)) {
				if (value.includes(node.value)) {
					const preventDefault = !EventManager.dispatch(this, EVENTS.PICKER.BEFORE_SELECT_OPTION, {
						detail: { option: node },
						cancelable: true,
					});

					if (!preventDefault) node.selected = true;
				} else if (isAttribute(node, 'selected')) {
					node.selected = false;
				}
			} else {
				if (node.value === value) {
					const preventDefault = !EventManager.dispatch(this, EVENTS.PICKER.BEFORE_SELECT_OPTION, {
						detail: { option: node },
						cancelable: true,
					});

					if (!preventDefault) node.selected = true;
				} else if (isAttribute(node, 'selected')) {
					node.selected = false;
				}
			}
		}

		this.update();

		EventManager.dispatch(this, 'input', { bubbles: true });
		EventManager.dispatch(this, EVENTS.PICKER.CHANGED);
	}

	get valueLabel() {
		if (this.multiple) {
			return Array.prototype.map
				.call(this.querySelectorAll('imt-option[selected]'), (node) => node.textContent)
				.join(', ');
		}

		return (this.querySelector('imt-option[selected]') || this.querySelector('imt-option.empty'))?.textContent;
	}

	get valueType() {
		if (this.multiple) {
			return Array.prototype.map.call(this.querySelectorAll('imt-option[selected]'), (node) =>
				node.getAttribute('data-type'),
			);
		} else {
			const selected = this.querySelector('imt-option[selected]');

			if (!selected) return null;
			return selected.getAttribute('data-type');
		}
	}

	get options() {
		return this._options;
	}

	set options(options) {
		this._options = options || [];
	}

	get actionOption() {
		return this._actionOption;
	}

	set actionOption(value) {
		this._actionOption = value;
	}

	get staticOptions() {
		const options = this.getAttribute('static-options');

		return options ? JSON.parse(options) : [];
	}

	set staticOptions(options) {
		this.setAttribute('static-options', JSON.stringify(options));
	}

	set inputField(element) {
		this._inputField = element;
	}

	set defaultClassList(list) {
		this._defaultClassList = list;
	}

	/**
	 * Registers a new component for custom behavior of the picker
	 * @param {Picker.COMPONENTS} type Name of the component
	 * @param {function} component Component fuctions
	 */

	register(type, component) {
		this.components[type] = component;
	}

	createPickerDisplay() {
		const displayElm = create('span.display');

		displayElm.onmouseenter = () => setTitleIfTextOverflows(displayElm, 'top', 'bottom');
		displayElm.onmouseleave = () => removeTitle(displayElm);

		this.appendChild(displayElm);
	}

	getPickerDisplay() {
		return this.querySelector(':scope > .display');
	}

	getOptionData() {
		const isGrouped = this.hasAttribute('grouped');
		const valueField = this.getAttribute('value-field');

		let option = null;

		if (isGrouped) {
			this._options?.store?.find((s) => {
				const opt = s?.options?.find((x) => x?.[valueField] === this.value);

				if (opt) {
					option = opt;

					return true;
				}

				return false;
			});
		}

		if (!option) {
			option = this._options?.store?.find((x) => x?.[valueField] === this.value && !x?.options);
		}

		return option;
	}

	// Sorts options by original index, then appends them to container as [selected…, unselected…].
	_applySelectionOrder(container, options) {
		const sorted = options.sort((a, b) => Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex));
		const selected = [];
		const unselected = [];

		for (const option of sorted) {
			if (option.hasAttribute('selected')) {
				selected.push(option);
			} else {
				unselected.push(option);
			}
		}

		for (const option of [...selected, ...unselected]) {
			container.appendChild(option);
		}
	}

	// Reorders options within an imt-optgroup: selected first, then unselected, both in original order.
	_reorderSelectedInGroup(group) {
		this._applySelectionOrder(group, Array.from(group.children));
	}

	// Reorders options within the flat options container (non-grouped): selected first, then unselected,
	// both in original order. The .select-all element is repositioned by reorderSelectedToTop after this runs.
	_reorderSelectedInFlatList(optionsContainer) {
		const options = Array.from(optionsContainer.children).filter((child) => child.nodeName === 'IMT-OPTION');

		this._applySelectionOrder(optionsContainer, options);
	}

	reorderSelectedToTop(optionsContainer) {
		if (!optionsContainer) return;

		const groups = optionsContainer.querySelectorAll('imt-optgroup');

		if (groups.length) {
			for (const group of groups) {
				this._reorderSelectedInGroup(group);
			}
		} else {
			this._reorderSelectedInFlatList(optionsContainer);
		}

		const selectAll = optionsContainer.querySelector(':scope > .select-all');

		if (selectAll) optionsContainer.prepend(selectAll);
	}

	_build() {
		if (this._built) return;
		this._built = true;

		let focusOutTimer;

		// Make switch focusable
		this.setAttribute('tabindex', 0);

		this.classList.add(...this._defaultClassList);

		this.createPickerDisplay();
		this.appendChild(create('span.messages'));

		const options = create('div.options');
		const actionOption = this.createActionOption();

		options.addEventListener('click', (event) => {
			const option = event.composedPath().find((node) => node.nodeName === 'IMT-OPTION');

			if (option) this.selectOptionElement(option);
		});

		if ((this.multiple || this.list) && !this.dropdown) {
			this.appendChild(options);
			if (actionOption) {
				this.appendChild(actionOption);
			}

			this.createSelectAll();
		} else {
			const dropdown = create('imt-picker-dropdown');

			this.appendChild(dropdown);

			dropdown.addEventListener('click', (event) => {
				event.stopPropagation();
			});

			const searcher = create('imt-searcher');

			if (this.searcherPlaceholder) {
				searcher.setAttribute('placeholder', this.searcherPlaceholder);
			}

			searcher.setAttribute('item', 'imt-option');
			searcher.setAttribute('group', 'imt-optgroup');
			searcher.container = options;

			dropdown.appendChild(searcher);

			this.createSelectAll(options);

			dropdown.appendChild(options);

			if (actionOption) {
				dropdown.appendChild(actionOption);
			}
		}

		const reload = create('button.btn.btn-sm.reload[type="button"]');

		reload.appendChild(create('i.fa.fa-fw.fa-sync'));
		this.appendChild(reload);
		reload.addEventListener('click', (event) => {
			this.reload(this.value);
			event.stopPropagation();
		});

		this.update();

		// Keyboard handlers
		this.addEventListener('keydown', (event) => {
			const hover = this.querySelector('imt-option.hover') || this.querySelector('imt-option[selected]');
			let next;

			switch (event.code) {
				case 'Space':
				case 'ArrowDown':
					if (this.classList.contains('expanded')) {
						if (event.code === 'ArrowDown') {
							if (hover) {
								if (hover.nextElementSibling) {
									next = hover.nextElementSibling;
								} else if (
									// Jump to next group in grouped select
									hover.parentNode.nodeName === 'IMT-OPTGROUP' &&
									hover.parentNode.nextElementSibling &&
									hover.parentNode.nextElementSibling.firstChild
								) {
									next = hover.parentNode.nextElementSibling.firstChild;
								} else {
									next = hover;
								}

								next.classList.add('hover');
							} else {
								const first = this.querySelector('imt-option');

								if (first) first.classList.add('hover');
							}

							event.stopPropagation();
							event.preventDefault();
						}
					} else {
						this.open();

						event.stopPropagation();
						event.preventDefault();
					}

					break;

				case 'ArrowUp':
					if (hover) {
						if (hover.previousElementSibling) {
							next = hover.previousElementSibling;
						} else if (
							// Jump to previous group in grouped select
							hover.parentNode.nodeName === 'IMT-OPTGROUP' &&
							hover.parentNode.previousElementSibling &&
							hover.parentNode.previousElementSibling.lastChild
						) {
							next = hover.parentNode.previousElementSibling.lastChild;
						} else {
							next = hover;
						}

						next.classList.add('hover');
					}

					event.stopPropagation();
					event.preventDefault();
					break;

				case 'Enter':
					if (hover) this.selectOptionElement(hover);
					this.focus();

					event.stopPropagation();
					event.preventDefault();
					break;

				case 'Escape':
					this.close();
					this.focus();

					event.stopPropagation();
					event.preventDefault();
					break;
			}
		});

		// Mouse/touch handlers
		this.addEventListener('click', (event) => {
			if (this.classList.contains('expanded')) {
				this.close();
			} else {
				this.open();
			}
			event.stopPropagation();
			event.preventDefault();
		});

		// Focus out handlers - handles possibility of wtiching between search and picker
		this.addEventListener('focusin', () => {
			clearTimeout(focusOutTimer);
		});

		this.addEventListener('focusout', () => {
			focusOutTimer = setTimeout(this.close.bind(this), 100);
		});
	}

	update() {
		const display = this.getPickerDisplay();
		const selectAll = this.parentElement?.querySelector('.select-all');

		if (!display) return;

		let value;
		let group;
		let defaultGroup = false;

		// If everything is selected -> check selectAll checkbox
		if (this.showSelectAll && selectAll && this.options.store) {
			const isGrouped = this.hasAttribute('grouped');
			const allOptions = isGrouped ? this.options.store.map((g) => g.options).flat() : this.options.store;

			if (this.value.length === allOptions.length) {
				selectAll.setAttribute('selected', '');
			} else {
				selectAll.removeAttribute('selected');
			}
		}

		const getPlainTextLabel = (childNotes, includeValue) =>
			Array.from(childNotes)
				.filter((child) => child.nodeType === Node.TEXT_NODE)
				.map((child) => {
					if (includeValue) {
						return child.textContent || child.value;
					} else {
						return child.textContent;
					}
				})
				.join('');

		// Resolve selected value
		if (this.multiple) {
			value = Array.from(this.querySelectorAll('imt-option[selected]'), (node) =>
				getPlainTextLabel(node.childNodes, true),
			).join(', ');
		} else {
			const selected = this.querySelector('imt-option[selected]');

			if (selected) {
				group = selected.closest('imt-optgroup')?.getAttribute('label');
				defaultGroup = selected.closest('imt-optgroup')?.getAttribute('default-group') === 'true';
				value = selected.classList.contains('empty')
					? selected.value
					: getPlainTextLabel(selected.childNodes, false) || selected.value;
			}
		}

		if (!value) {
			display.classList.add('text-muted');
			display.textContent = this.getAttribute('placeholder') || '';
		} else {
			display.classList.remove('text-muted');
			display.textContent = group && !defaultGroup && this.displayGroup !== false ? `${group}: ${value}` : value;
		}

		if (this.components[Picker.COMPONENTS.DISPLAY_CHANGED]) {
			this.components[Picker.COMPONENTS.DISPLAY_CHANGED](display, value);
		}
	}

	async open() {
		this.displaySearcher();

		if ((this.multiple || this.list) && !this.dropdown) return;
		if (this.readonly || this.disabled) return;
		if (this.classList.contains('expanded')) return;

		const dropdown = this.querySelector('imt-picker-dropdown');
		const rect = this.getBoundingClientRect();
		const MAX_DROPDOWN_HEIGHT_PX = 165;
		const BOTTOM_MARGIN_PX = 32;
		const maxHeight = window.innerHeight - rect.bottom - BOTTOM_MARGIN_PX;

		dropdown.style.maxHeight = `${Math.max(MAX_DROPDOWN_HEIGHT_PX, maxHeight)}px`;

		if (this.hasAttribute('provider')) {
			const rpcData =
				typeof this.components[Picker.COMPONENTS.RPC_DATA_GETTER] === 'function'
					? this.components[Picker.COMPONENTS.RPC_DATA_GETTER]() || {}
					: {};

			if (this.hasAttribute('provider') && (!this._loaded || !deepEqual(rpcData, this._loadedRpcData))) {
				if (this.value && this._loaded) {
					this.reload(this.value);
				}
				this.reload(undefined, false);
			}
		}

		if (this._isMultiselectDropdown) {
			this.reorderSelectedToTop(this.querySelector(':scope .options'));
		}

		dropdown.open();
		this.classList.add('expanded');

		animateElementHeight(dropdown, { timing: 150 }).then(() => {
			setTimeout(() => {
				this._focusSearcherIfExists();
			}, 1);
		});
	}

	async reload(value, resolveDefaultValue) {
		if (this.classList.contains('loading')) return;
		if (!this.hasAttribute('provider')) return;
		this.classList.add('loading');
		this._inputField && this._inputField.form.loadings++;

		try {
			// Create abort controller so we can abort the request if needed
			this._abortController = new AbortController();

			const form = this.closest('imt-forman');
			const context = form ? form.meta.imlContext : {};
			const buildTimeVariables = form?.meta?.imlOptions?.buildTimeVariables || {};

			const formData = this._inputField?.form?.meta?.data || {};
			this._loadedRpcData = this.components[Picker.COMPONENTS.RPC_DATA_GETTER]() || {};

			const panel = form.closest('imt-panel.remote');
			const allowApiCall =
				this._inputField?.allowApiCall ||
				panel?.allowApiCall ||
				(this._inputField?.getAttribute && this._inputField?.getAttribute('extended-type') === 'true');

			let response = await Loader.guardedLoad(
				this.getAttribute('provider'),
				{
					signal: this._abortController.signal,
					data: { ...formData, ...this._loadedRpcData },
					accountParamName: this.closest('imt-input-account')?.name || form?.meta?.moduleParameters?.[0]?.name,
					context,
					buildTimeVariables,
				},
				allowApiCall,
			);

			const wrapper = this.getAttribute('wrapper-field');

			if (wrapper && response[wrapper]) response = response[wrapper];

			// Fetch done, remove abort controller
			this._abortController = undefined;
			this._loaded = true;

			// Remove old options
			this.querySelectorAll('imt-option:not(.action-option > imt-option)').forEach((option) => {
				// Don't remove temporary options
				if (!option.temporary) {
					if (option.selected) {
						// Convert selected option to temporary so the value is preserved
						option.temporary = true;
					} else {
						option.parentNode.removeChild(option);
					}
				}
			});

			// Remove old optgroup
			this.querySelectorAll('imt-optgroup').forEach((group) => {
				group.parentElement.removeChild(group);
			});

			if (!Array.isArray(response)) return;

			let options = Picker.normalizeOptions(response, this.options);

			if (this.components[Picker.COMPONENTS.OPTIONS_NORMALIZED]) {
				options = this.components[Picker.COMPONENTS.OPTIONS_NORMALIZED](options);
			}

			this.addOptions(options, value, {}, resolveDefaultValue);

			this.update();

			if (this.list) {
				dispatchResizeDebounced(this);
			}

			EventManager.dispatch(this, EVENTS.PICKER.LOAD_DONE, { detail: options });
			const dropdown = this.querySelector('imt-picker-dropdown');

			this.displaySelectAll();

			if (dropdown?._opened) {
				this.displaySearcher();
				dropdown.updatePosition();
				this.focus();
				this._focusSearcherIfExists();
			}

			this.showError(false);
		} catch (err) {
			if (err.name === 'AbortError') return;

			EventManager.dispatch(this, EVENTS.PICKER.LOAD_ERROR, { detail: err });
			this.showError(err);

			throw err;
		} finally {
			this.classList.remove('loading');
			this._inputField && this._inputField.form.loadings--;
		}
	}

	_focusSearcherIfExists() {
		const searcher = this.querySelector(':scope > imt-picker-dropdown > imt-searcher:not([hidden])');

		if (searcher) {
			searcher.focus();
		}
	}

	close() {
		if ((this.multiple || this.list) && !this.dropdown) return;
		this.classList.remove('expanded');
		this.querySelector('imt-picker-dropdown').close();

		const searcher = this.querySelector('imt-searcher');

		if (searcher) {
			searcher.clear();
		}

		// Abort loading of external provider
		if (this._abortController) this._abortController.abort();
	}

	_buildOptionDecorations(element, item, description, pill) {
		if (item.icon) {
			const icon = create(`i.mr-2.fa-regular.fa-${item.icon}`);

			element.insertBefore(icon, element.childNodes[0]);
		}

		const wrapper = create('div.description-wrapper');

		if (description && item[description]) {
			const subtitle = create('div.small.text-muted.text-truncate.line-clamp');

			subtitle.textContent = item[description];
			wrapper.append(subtitle);
		}

		if (pill && item[pill]) {
			const pillElement = create(`imt-pill.status-pill.${item[pill].toLowerCase()}`);

			pillElement.textContent = item[pill];
			wrapper.append(pillElement);
		}

		return wrapper;
	}

	renderOption(item, { temporary = false, parent, restoreData = {}, originalIndex = 0 } = {}) {
		if (!parent) {
			parent = this.querySelector('div.options') || this;
		}

		const labelField = this.getAttribute('label-field') || 'label';
		const valueField = this.getAttribute('value-field') || 'value';
		const descriptionField = this.getAttribute('description-field') || 'description';
		const pillField = this.getAttribute('pill-field') || 'pill';

		if ((this.multiple || this.list) && this.options.nested && item.nested) {
			throw new Error(
				'Invalid configuration. It is not possible to define common nested and option nested together for multiple select or list.',
			);
		}

		// if there is a temporary option with current value, convert it to non temporary and update it's text content
		const temporaryOption = parent.querySelector(`imt-option[temporary]`);

		if (temporaryOption && temporaryOption.value === item[valueField]) {
			temporaryOption.temporary = false;
			temporaryOption.dataset.originalIndex = originalIndex;
			temporaryOption.textContent = item[labelField] || item[valueField];

			const description = this.options.description || descriptionField;
			const pill = this.options.pill || pillField;
			const wrapper = this._buildOptionDecorations(temporaryOption, item, description, pill);

			if (description || pill) {
				temporaryOption.append(wrapper);
			}

			if (this.components[Picker.COMPONENTS.OPTION_RENDERER]) {
				this.components[Picker.COMPONENTS.OPTION_RENDERER](temporaryOption, item, this.querySelector('div.options'));
			}

			return temporaryOption;
		}

		const option = create('imt-option');

		if (item.empty) {
			option.classList.add('empty');
			option.value = item[this.options.value || valueField];
			option.textContent = item[labelField] || item[valueField];
		} else {
			// Store option metadata as attributes
			if (this.options.data) {
				Object.entries(this.options.data).forEach(([key, variable]) => {
					option.setAttribute(`data-${key}`, item[variable]);
				});
			}

			option.value = item[this.options.value || valueField];
			option.textContent = item[labelField] || item[valueField];

			const description = this.options.description || descriptionField;
			const pill = this.options.pill || pillField;

			option.append(this._buildOptionDecorations(option, item, description, pill));

			if (!temporary && this.components[Picker.COMPONENTS.OPTION_RENDERER]) {
				this.components[Picker.COMPONENTS.OPTION_RENDERER](option, item, this.querySelector('div.options'));
			}
		}

		if (this.showValue) {
			let displayValue = option.value;

			if (this.components[Picker.COMPONENTS.OPTION_DISPLAY_VALUE]) {
				displayValue = this.components[Picker.COMPONENTS.OPTION_DISPLAY_VALUE](option);
			}
			const valueSpan = create(`span.option-value`);

			valueSpan.textContent = ` (${displayValue})`;

			if (option.childNodes.length > 1) {
				option.insertBefore(valueSpan, option.childNodes[1]);
			} else {
				option.appendChild(valueSpan);
			}
		}

		if (restoreData) {
			Object.entries(restoreData).forEach(([key, variable]) => {
				option.setAttribute(`data-${key}`, variable);
			});
		}

		// Prepare container for nested fieldsets if there are any
		const nestedInstructions = item.nested || this.options.nested;

		if (nestedInstructions) {
			const wrapper = this._inputField || this.parentNode;

			if (!wrapper.querySelector('imt-nested')) {
				wrapper.appendChild(create('imt-nested'));
			}
		}

		if (temporary) option.temporary = true;
		option.dataset.originalIndex = originalIndex;
		parent.appendChild(option);

		return option;
	}

	createSelectAll(options) {
		if (!this.multiple) {
			return;
		}
		const selectAll = create('div.select-all');

		selectAll.textContent = I18n.l('picker.selectAll');
		selectAll.addEventListener('click', () => {
			const options = this.querySelectorAll('imt-option:not(.action-option > imt-option)');

			if (selectAll.hasAttribute('selected')) {
				selectAll.removeAttribute('selected');
			} else {
				selectAll.setAttribute('selected', '');
			}

			const isChecked = selectAll.hasAttribute('selected');

			for (const node of options) {
				if (node.hasAttribute('selected') === isChecked) continue;
				this.selectOptionElement(node);
			}
		});

		if (options) {
			options.append(selectAll);
		} else {
			this.before(selectAll);
		}
	}

	createActionOption() {
		if (!this.actionOption) return;

		const actionOptionEl = create('div.action-option');
		const option = create('imt-option');
		const plusButton = create('i.fa.fa-plus');
		const textSpan = create('span');

		this.classList.add('has-action-option');
		option.appendChild(plusButton);
		option.appendChild(textSpan);
		textSpan.textContent = this.actionOption.label;
		actionOptionEl.appendChild(option);

		actionOptionEl.addEventListener('click', () => {
			if (this.actionOption.event) {
				EventManager.dispatch(this, this.actionOption.event, { bubbles: true });
			} else if (this.actionOption.action) {
				this.actionOption.action(this);
			}
			this.close();
		});

		return actionOptionEl;
	}

	async addOptions(options, defaultValue, metadata = {}, resolveDefaultValue = true) {
		// Store options for further rendering of nested fieldsets
		this.sortOptions(options);
		this.options = options;

		const valueField = this.getAttribute('value-field') || 'value';
		const labelField = this.getAttribute('label-field') || 'label';

		let defaultValueResolved = false;

		if (resolveDefaultValue && typeof defaultValue === 'undefined') {
			defaultValue = this.resolveDefaultValue(options, valueField);
			defaultValueResolved = !!defaultValue;
		}

		// Choose value if static options and defaultValue matches one of option value
		// Default value for multiple select can be simple value or array
		const shouldAddTemporary =
			typeof defaultValue !== 'undefined' &&
			'string' === typeof options.store &&
			!(this.staticOptions || []).find((o) => o.value === defaultValue);

		if (
			shouldAddTemporary &&
			(!Array.isArray(options.store) ||
				(this.hasAttribute('grouped') ? options.store.map((g) => g.options).flat() : options.store).some(
					(o) =>
						o &&
						((Array.isArray(defaultValue) && defaultValue.includes(o[valueField])) || o[valueField] === defaultValue),
				))
		) {
			/**
			 * If the value is set, we need to add temporary option so the value is visible
			 * until options are fetched (both synchronously and asynchronously). That also prevents
			 * value clearing when user doesn't open a dropdown so options are actually fetched.
			 */

			if (this.multiple && Array.isArray(defaultValue)) {
				// We have an array of values for multi-select
				defaultValue.map((v) => this.renderOption({ value: v, label: v }, { temporary: true }));
			} else {
				// Just a single value is single-select
				this.renderOption(
					{
						[valueField || 'value']: defaultValue,
						[labelField || 'label']: metadata.restore?.label || defaultValue,
					},
					{ temporary: true, restoreData: metadata.restore?.data },
				);
			}
		}

		if (Array.isArray(options.store)) {
			options.store = this.staticOptions.concat(options.store);

			let flatIndex = 0;

			for (const item of options.store) {
				if (this.hasAttribute('grouped') && item.options) {
					const group = create('imt-optgroup');

					group.setAttribute('label', item.label);
					if (item.id) {
						group.setAttribute('id', item.id);
					}
					if (item.defaultGroup) {
						group.setAttribute('default-group', item.defaultGroup);
					}

					this.appendChild(group);

					if (Array.isArray(item.options)) {
						for (const [subIndex, subitem] of item.options.entries()) {
							if (item.default) defaultValue = item.value;
							this.renderOption(subitem, { parent: group, originalIndex: subIndex });
						}
					}
				} else {
					if (item.default && (typeof defaultValue === 'undefined' || defaultValue === '')) defaultValue = item.value;
					this.renderOption(item, { originalIndex: flatIndex++ });
				}
			}

			if (options.store.length === 0 && this.list) {
				const empty = create('div.empty-list.list-group-item');

				empty.textContent = I18n.l('picker.listEmpty');
				this.querySelector('div.options').append(empty);
			}
		}

		if (typeof defaultValue !== 'undefined') {
			this.value = defaultValue;

			if (defaultValueResolved && this._inputField) {
				this._inputField.setValueToFormValues();
			}
		}

		this.displaySelectAll();
	}

	loadCustomOptions(options, defaultValue) {
		if (!this._loaded && this.hasAttribute('provider')) {
			this._loaded = true;
		}

		this.addOptions(options, defaultValue);
	}

	resolveDefaultValue(options, valueField) {
		const value = this.multiple ? [] : undefined;
		const resolver = this.components[Picker.COMPONENTS.DEFAULT_OPTION_RESOLVER];

		if (!resolver || !(Array.isArray(options.store) && options.store.length)) return value;

		for (const option of options.store) {
			if (resolver(option)) {
				if (this.multiple) {
					value.push(option[valueField]);
				} else {
					return option[valueField];
				}
			} else if (Array.isArray(option.options)) {
				for (const groupOption of option.options) {
					if (resolver(groupOption)) {
						if (this.multiple) {
							value.push(option[valueField]);
						} else {
							return groupOption[valueField];
						}
					}
				}
			}
		}
		return value;
	}

	selectOptionElement(option, close = true) {
		if (isAttribute(option, 'disabled')) return;

		const preventDefault = !EventManager.dispatch(this, EVENTS.PICKER.CHANGE, {
			detail: { element: option },
			cancelable: true,
		});

		if (preventDefault) return;

		const selected = option.hasAttribute('selected');

		if (!(this.multiple || this.list) && selected) {
			this.close();
			return;
		}

		if (this.multiple && this.showSelectAll && option.hasAttribute('selected')) {
			const node = this.parentElement?.querySelector('.select-all') ?? this.querySelector('.select-all');

			node?.removeAttribute('selected');
		}

		if (this.multiple) {
			if (selected) {
				option.removeAttribute('selected');
			} else {
				option.setAttribute('selected', '');
			}
		} else {
			// Not a multiselect - unselect other options
			for (const child of this.querySelectorAll('imt-option')) {
				if (child !== option) child.removeAttribute('selected');
			}
			option.setAttribute('selected', '');
		}

		EventManager.dispatch(this, 'input', { bubbles: true });
		if (close && !this.multiple && !this.list) this.close();
		this.update();

		EventManager.dispatch(this, EVENTS.PICKER.CHANGED, { detail: { element: option } });

		if (this.components[Picker.COMPONENTS.RPC_DATA_GETTER]) {
			this._loadedRpcData = this.components[Picker.COMPONENTS.RPC_DATA_GETTER]();
		}
	}

	getSelectedElements() {
		return Array.from(this.querySelectorAll(':scope imt-option[selected]'));
	}

	showError(error) {
		if (!error) {
			this.removeAttribute('error');
			return;
		}

		const messages = this.querySelector(':scope > .messages');

		if (messages) messages.textContent = I18n.l('picker.failedLoad');
		this.setAttribute('error', '');
	}

	displaySearcher() {
		const searcher = this.querySelector('imt-searcher');

		if (searcher) {
			if (this.showSearcher === true) {
				searcher.hidden = false;
			} else if (this.showSearcher === false) {
				searcher.hidden = true;
			} else if (!isNaN(this.showSearcher)) {
				const minLength = +this.showSearcher;

				searcher.hidden =
					Array.isArray(this.options?.store) &&
					this.options.store.flatMap((item) => item.options || item).length < minLength;
			}
		}
	}

	displaySelectAll() {
		const selectAll = this.parentElement?.querySelector('.select-all');

		if (selectAll) {
			if (this.showSelectAll === true) {
				selectAll.hidden = false;
			} else if (this.showSelectAll === false) {
				selectAll.hidden = true;
			} else if (!isNaN(this.showSelectAll)) {
				const minLength = +this.showSelectAll;

				selectAll.hidden =
					Array.isArray(this.options?.store) &&
					this.options.store.flatMap((item) => item.options || item).length < minLength;
			}
		}
	}

	sortOptions(options) {
		const sort = this._inputField?._instructions?.sort;
		const sorter = (opts) => {
			if (sort === 'text') {
				return opts.sort((a, b) => a.value.toString().localeCompare(b.value.toString()));
			} else if (sort === 'number') {
				return opts.sort((a, b) => a.value - b.value);
			}
			return opts;
		};

		if (Array.isArray(options.store)) {
			if (this.hasAttribute('grouped')) {
				options.store.forEach((opt) => {
					opt.options = sorter(opt.options);
				});
			} else {
				options.store = sorter(options.store);
			}
		}
	}
}

Picker.COMPONENTS = {
	RPC_DATA_GETTER: 'rpcData',
	OPTION_RENDERER: 'optionRenderer',
	DEFAULT_OPTION_RESOLVER: 'defaultOptionResolver',
	DISPLAY_CHANGED: 'displayChanged',
	OPTIONS_NORMALIZED: 'optionsNormalized',
	OPTION_DISPLAY_VALUE: 'optionDisplayValue',
};

export class PickerDropdown extends HTMLElement {
	/** @type {Node[] | undefined} */
	_scrollables;

	constructor() {
		super();

		this.updatePosition = (dropdownRect = this.getBoundingClientRect()) => {
			const rect = this.parentNode.getBoundingClientRect();
			const yDifference = window.innerHeight - (rect.y + rect.height);

			if (yDifference < Math.max(dropdownRect.height, 250)) {
				this.style.left = `${rect.x}px`;
				this.style.top = `${rect.top - 1 - dropdownRect.height}px`;
				this.style.width = `${Math.max(200, this.parentNode.offsetWidth)}px`;
				this.classList.add('top-aligned');
			} else {
				this.style.left = `${rect.x}px`;
				this.style.top = `${rect.bottom - 1}px`;
				this.style.width = `${Math.max(200, this.parentNode.offsetWidth)}px`;
				this.classList.remove('top-aligned');
			}
		};

		// Remove .hover class used for focus of elements while navigation via keyboard
		this._observer = new MutationObserver((mutationList) => {
			const hoverMutation = mutationList.find(
				(mutation) => mutation.target.classList.contains('hover') && mutation.attributeName === 'class',
			);
			const styleMutation = mutationList.find(
				(mutation) => mutation.attributeName === 'style' && mutation.target !== this,
			);

			if (hoverMutation) {
				this.querySelectorAll('imt-option.hover').forEach((elm) => {
					if (elm !== hoverMutation.target) elm.classList.remove('hover');
				});
			}

			if (styleMutation) {
				this.updatePosition();
			}
		});

		this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
	}

	get opened() {
		return this._opened;
	}

	open() {
		const dropdownRect = this.getBoundingClientRect();

		this._opened = true;
		this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
		this._observer.observe(document.body, { attributes: true, attributeFilter: ['style'], subtree: true });

		window.addEventListener('resize', this.updatePosition);
		window.addEventListener('scroll', this.updatePosition);

		// Detect all scrollable elements up the dom tree and attach scroll event to them
		this._scrollables = [];
		let node = this.parentNode;

		while (node && node !== window.document) {
			if (['auto', 'scroll'].includes(window.getComputedStyle(node).overflowY)) {
				this._scrollables.push(node);
				node.addEventListener('scroll', this.updatePosition);
			}
			node = node.parentNode;
		}

		// Remove hover class used for keyboard navigation
		this.querySelectorAll('imt-option').forEach((e) => e.classList.remove('hover'));

		// update position and height in requestAnimationFrame to ensure that the requeired values (like scrollHeight)
		// are properly calculated and to avoid flickering issues
		// setTimeout is used to schedule the style change to the next event loop cycle
		setTimeout(() => {
			requestAnimationFrame(() => {
				const rect = this.getBoundingClientRect(dropdownRect);

				if (rect.height < 200 && this.querySelector('.options').scrollHeight > 200) {
					this.style.minHeight = '200px';
				}
				this.updatePosition();
			});
		});
	}

	close() {
		this._opened = false;
		window.removeEventListener('resize', this.updatePosition);
		window.removeEventListener('scroll', this.updatePosition);
		this._observer.disconnect();
		this.style.minHeight = '';

		if (Array.isArray(this._scrollables)) {
			for (const node of this._scrollables) {
				node.removeEventListener('scroll', this.updatePosition);
			}
		}
	}
}

registerCustomElement('imt-picker', Picker);
registerCustomElement('imt-picker-dropdown', PickerDropdown);
registerCustomElement('imt-option', Option);
registerCustomElement('imt-optgroup', OptionGroup);
